import { useState, useEffect } from "react";
import {
  X,
  FileText,
  Truck,
  Users,
  Building2,
  Calendar,
  DollarSign,
  AlertTriangle,
  Save,
  Eye,
  Settings,
} from "lucide-react";
import Swal from "sweetalert2";
import { CustomFieldsBuilder } from "./CustomFieldsBuilder";

export const DocumentTypeModal = ({
  mode,
  documentType,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [formData, setFormData] = useState({
    entity_type: "vehicle",
    name: "",
    code: "",
    description: "",
    expiration_type: "date",
    validity_period_days: "",
    validity_period_months: "",
    validity_mileage: "",
    requires_cost: false,
    requires_provider: false,
    is_mandatory: false,
    activo: true,
  });
  const [customFields, setCustomFields] = useState([]);
  const [alertConfig, setAlertConfig] = useState([
    { level: "warning", days_before: 30 },
    { level: "critical", days_before: 7 },
    { level: "expired", days_before: 0 },
  ]);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  useEffect(() => {
    if (mode === "edit" || mode === "view") {
      setFormData({
        entity_type: documentType.entity_type,
        name: documentType.name,
        code: documentType.code,
        description: documentType.description || "",
        expiration_type: documentType.expiration_type,
        validity_period_days: documentType.validity_period_days || "",
        validity_period_months: documentType.validity_period_months || "",
        validity_mileage: documentType.validity_mileage || "",
        requires_cost: documentType.requires_cost,
        requires_provider: documentType.requires_provider,
        is_mandatory: documentType.is_mandatory,
        activo: documentType.activo,
      });
      setCustomFields(documentType.custom_fields || []);
      setAlertConfig(
        documentType.alert_config || [
          { level: "warning", days_before: 30 },
          { level: "critical", days_before: 7 },
          { level: "expired", days_before: 0 },
        ],
      );
    }
  }, [mode, documentType]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!formData.name || !formData.code) {
      Swal.fire("Error", "Completa los campos obligatorios", "error");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        custom_fields_config: customFields,
        alert_config: alertConfig,
      };

      const url =
        mode === "create"
          ? `${API_URL}/document-types`
          : `${API_URL}/document-types/${documentType.id}`;

      const method = mode === "create" ? "POST" : "PUT";

      const response = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: mode === "create" ? "¡Tipo Creado!" : "¡Tipo Actualizado!",
          text: `El tipo de documento ha sido ${mode === "create" ? "creado" : "actualizado"} exitosamente`,
          timer: 2000,
          showConfirmButton: false,
        });
        onSuccess();
      } else {
        const error = await response.json();
        throw new Error(error.message || "Error al guardar");
      }
    } catch (error) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === "view";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FileText className="h-6 w-6 text-white mr-3" />
              <h3 className="text-xl font-bold text-white">
                {mode === "create"
                  ? "Nuevo Tipo de Documento"
                  : mode === "edit"
                    ? "Editar Tipo de Documento"
                    : "Ver Tipo de Documento"}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab("general")}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${activeTab === "general"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <Settings className="h-4 w-4 inline mr-2" />
              General
            </button>
            <button
              onClick={() => setActiveTab("custom-fields")}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${activeTab === "custom-fields"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <FileText className="h-4 w-4 inline mr-2" />
              Campos Personalizados
              {customFields.length > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full text-xs">
                  {customFields.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("alerts")}
              className={`px-6 py-3 font-medium text-sm transition-colors relative ${activeTab === "alerts"
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              Alertas
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {/* TAB: GENERAL */}
          {activeTab === "general" && (
            <div className="space-y-6">
              {/* Tipo de Entidad */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Tipo de Entidad *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    disabled={isReadOnly || mode === "edit"}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        entity_type: "vehicle",
                      }))
                    }
                    className={`p-4 border-2 rounded-xl transition-all ${formData.entity_type === "vehicle"
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-blue-300"
                      } ${isReadOnly || mode === "edit" ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <Truck className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                    <span className="text-sm font-semibold">Vehículo</span>
                  </button>

                  <button
                    type="button"
                    disabled={isReadOnly || mode === "edit"}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        entity_type: "driver",
                      }))
                    }
                    className={`p-4 border-2 rounded-xl transition-all ${formData.entity_type === "driver"
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 hover:border-purple-300"
                      } ${isReadOnly || mode === "edit" ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <Users className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                    <span className="text-sm font-semibold">Conductor</span>
                  </button>

                  <button
                    type="button"
                    disabled={isReadOnly || mode === "edit"}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        entity_type: "company",
                      }))
                    }
                    className={`p-4 border-2 rounded-xl transition-all ${formData.entity_type === "company"
                        ? "border-green-500 bg-green-50"
                        : "border-gray-200 hover:border-green-300"
                      } ${isReadOnly || mode === "edit" ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <Building2 className="h-8 w-8 mx-auto mb-2 text-green-500" />
                    <span className="text-sm font-semibold">Empresa</span>
                  </button>
                </div>
              </div>

              {/* Nombre y Código */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">
                    Nombre *
                  </label>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    required
                    placeholder="Ej: SOAT"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label htmlFor="code" className="block text-sm font-bold text-gray-700 mb-2">
                    Código *
                  </label>
                  <input
                    id="code"
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    disabled={isReadOnly || mode === "edit"}
                    required
                    placeholder="Ej: SOAT_VEH"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 uppercase"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El código no puede modificarse después de crear el tipo
                  </p>
                </div>
              </div>

              {/* Descripción */}
              <div>
                <label htmlFor="description" className="block text-sm font-bold text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  rows={3}
                  placeholder="Descripción del tipo de documento..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                />
              </div>

              {/* Tipo de Vencimiento */}
              <div>
                <label htmlFor="expiration_type" className="block text-sm font-bold text-gray-700 mb-2">
                  Tipo de Vencimiento *
                </label>
                <select
                  id="expiration_type"
                  name="expiration_type"
                  value={formData.expiration_type}
                  onChange={handleInputChange}
                  disabled={isReadOnly}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                >
                  <option value="date">📅 Por Fecha</option>
                  <option value="mileage">🛣️ Por Kilometraje</option>
                  <option value="both">📅🛣️ Ambos</option>
                </select>
              </div>

              {/* Validez */}
              {(formData.expiration_type === "date" ||
                formData.expiration_type === "both") && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="validity_period_days" className="block text-sm font-bold text-gray-700 mb-2">
                        Validez (Días)
                      </label>
                      <input
                        id="validity_period_days"
                        type="number"
                        name="validity_period_days"
                        value={formData.validity_period_days}
                        onChange={handleInputChange}
                        disabled={isReadOnly}
                        min="1"
                        placeholder="365"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                      />
                    </div>

                    <div>
                      <label htmlFor="validity_period_months" className="block text-sm font-bold text-gray-700 mb-2">
                        Validez (Meses)
                      </label>
                      <input
                        id="validity_period_months"
                        type="number"
                        name="validity_period_months"
                        value={formData.validity_period_months}
                        onChange={handleInputChange}
                        disabled={isReadOnly}
                        min="1"
                        placeholder="12"
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                )}

              {(formData.expiration_type === "mileage" ||
                formData.expiration_type === "both") && (
                  <div>
                    <label htmlFor="validity_mileage" className="block text-sm font-bold text-gray-700 mb-2">
                      Validez por Kilometraje
                    </label>
                    <input
                      id="validity_mileage"
                      type="number"
                      name="validity_mileage"
                      value={formData.validity_mileage}
                      onChange={handleInputChange}
                      disabled={isReadOnly}
                      min="1"
                      placeholder="10000"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100"
                    />
                  </div>
                )}

              {/* Checkboxes */}
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="requires_cost"
                    checked={formData.requires_cost}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    <DollarSign className="h-4 w-4 inline mr-1" />
                    Requiere registro de costo
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="requires_provider"
                    checked={formData.requires_provider}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Requiere proveedor
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_mandatory"
                    checked={formData.is_mandatory}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Es obligatorio
                  </span>
                </label>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="activo"
                    checked={formData.activo}
                    onChange={handleInputChange}
                    disabled={isReadOnly}
                    className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">
                    Activo
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* TAB: CAMPOS PERSONALIZADOS */}
          {activeTab === "custom-fields" && (
            <CustomFieldsBuilder
              fields={customFields}
              onChange={setCustomFields}
              disabled={isReadOnly}
            />
          )}

          {/* TAB: ALERTAS */}
          {activeTab === "alerts" && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  Configura cuántos días antes del vencimiento se enviarán las
                  alertas
                </p>
              </div>

              {alertConfig.map((alert, index) => (
                <div
                  key={index}
                  className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <label htmlFor={`alert_level_${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Nivel
                    </label>
                    <select
                      id={`alert_level_${index}`}
                      value={alert.level}
                      onChange={(e) => {
                        const newConfig = [...alertConfig];
                        newConfig[index].level = e.target.value;
                        setAlertConfig(newConfig);
                      }}
                      disabled={isReadOnly}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="info">ℹ️ Info</option>
                      <option value="warning">⚠️ Advertencia</option>
                      <option value="critical">🚨 Crítico</option>
                      <option value="expired">❌ Vencido</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor={`alert_days_${index}`} className="block text-sm font-medium text-gray-700 mb-2">
                      Días antes
                    </label>
                    <input
                      id={`alert_days_${index}`}
                      type="number"
                      value={alert.days_before}
                      onChange={(e) => {
                        const newConfig = [...alertConfig];
                        newConfig[index].days_before = parseInt(e.target.value);
                        setAlertConfig(newConfig);
                      }}
                      disabled={isReadOnly}
                      min="0"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              ))}

              {!isReadOnly && (
                <button
                  type="button"
                  onClick={() =>
                    setAlertConfig([
                      ...alertConfig,
                      { level: "warning", days_before: 15 },
                    ])
                  }
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                >
                  + Agregar Alerta
                </button>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-lg font-semibold transition-colors"
          >
            {isReadOnly ? "Cerrar" : "Cancelar"}
          </button>
          {!isReadOnly && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="px-6 py-3 text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {mode === "create" ? "Crear Tipo" : "Guardar Cambios"}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
